import md5 from "md5";

/**
 * Deterministic 20-char rowId for the unified opsctrl write model. Mirrors the
 * server twin (efb-app-server/src/db/rowid.ts) byte-for-byte: npm `md5` and
 * node `crypto.createHash('md5')` produce identical digests, so the same
 * logical row written natively (local DB) and from the web SPA (shared server)
 * collapses onto one rowId. Parts are coerced via String() and joined by '|'.
 *
 * rowId hashes the logical key only — registrationId is NOT included (flightId
 * is already globally unique, and registrationId is the orthogonal tenant
 * column enforced by applyOps' tenant guard on the server).
 */
export function rowIdFor(parts: Array<string | number>): string {
  return md5(parts.map(String).join("|")).substring(0, 20);
}

/**
 * Single source of truth for the deterministic rowId composition of each opsctrl
 * table on the unified write model. The ORDER of key columns is load-bearing:
 * rowIdFor() is order-sensitive, and the identical order must be used by the
 * native write call-sites (local DB) and the web SPA (which posts the
 * precomputed rowId to the shared server). registrationId is intentionally NOT
 * a key column — flightId/dutyId are already globally unique and registrationId
 * is the orthogonal tenant column (enforced by the server's tenant guard).
 * Mirror of the logical keys in efb-app-server/src/sync/tables.ts.
 *
 * Lives here (dep-free module) rather than in opsctrl-foc/functions.ts so the
 * rowId backfill (rowid-backfill.ts) can consume it without dragging the
 * Angular/Capacitor singletons that functions.ts imports.
 */
export const ROWID_KEY_SPEC: Record<string, string[]> = {
  // The six calculation reports are APPEND-ONLY again (2026-08-13), restoring
  // what 6c961b06 removed: `Timestamp` is in the key, so each recalculation
  // mints a fresh rowId and lands as a NEW version instead of overwriting the
  // previous one. The identity part of the key is unchanged — nav/met/notam are
  // one-per-flight, fuel/perf/loading one-per-flight-per-mission-type.
  //
  // The reads decide which version is CURRENT rather than relying on there
  // being exactly one live row: highest `Timestamp` among `obsolete = 0`, per
  // identity group. That is what makes appending safe. The obvious alternative
  // — obsolete the old row, then insert the new one — is TWO ops, and since
  // D1/D2 each op is separately SAVEPOINTed and separately rejectable, so a
  // half-applied pair leaves the old row obsoleted with NO new row and the
  // flight reads empty. With currency derived, both post-failure states are
  // correct: the new version landed and is current, or it did not and the
  // previous one still is.
  //
  // The inserts keep `ON CONFLICT(rowId) DO UPDATE` as a same-millisecond
  // safety net — two calculations of one logical row inside the same ms would
  // otherwise raise a UNIQUE violation and fail a legitimate recalculation.
  opsctrl_flightopscontroller_nav: ["flightId", "Timestamp"],
  opsctrl_flightopscontroller_met: ["flightId", "Timestamp"],
  opsctrl_flightopscontroller_notam: ["flightId", "Timestamp"],
  opsctrl_flightopscontroller_fuel: ["flightId", "opsMissionType", "Timestamp"],
  opsctrl_flightopscontroller_perf: ["flightId", "opsMissionType", "Timestamp"],
  opsctrl_flightopscontroller_loading: ["flightId", "opsMissionType", "Timestamp"],
  opsctrl_flightopscontroller_navseg: ["flightId", "legId"],
  opsctrl_flightopscontroller_loadingelements: ["flightId", "opsMissionType", "parameterHash"],
  opsctrl_flightopscontroller_param: ["parameterHash", "paramTypeId", "tableSuffix", "opsMissionType"],
  opsctrl_flightcrewcontroller_crewmemberdata: ["dutyId", "crewMemberId", "keyName"],
  // Duty signing is REPEATABLE (2026-08-13). `created` is in the key on purpose:
  // it is the sign timestamp, so every sign computes a FRESH rowId and lands as
  // a new signature record instead of colliding with the previous one.
  //
  // This replaces the 2026-06-05 first-writer-wins key (["ident", "dutyFrom"]),
  // under which a second sign of the same Dienstplan hit the same rowId and the
  // server rejected it `immutable` — making a duty signable exactly once per
  // (PIN, aircraft, dutyFrom), forever. That gate enforced no regulatory
  // requirement (the rule is "every flight must be signed", not "a signature is
  // immutable") and is now removed server-side too (`ops.ts`). A NARROWER
  // `immutable` reject replaced it on 2026-08-14 — an upsert that would change
  // `ident`, `dutyFrom`, `created`, `pilotInCommand` or `signature` on an
  // EXISTING row. A genuine re-sign carries a fresh `created`, so it mints a
  // different rowId and never meets that clause; repeatability is unaffected.
  //
  // Signature records are therefore append-only by key, which is what lets a
  // calculation freeze its own attribution: `signatureId` on the report tables
  // stores the flightCrewId in force when it ran, instead of resolving through
  // flightlist.flightCrewId — a pointer that moves on every re-sign.
  //
  // Listing it here makes runRegistrationMigrationScripts add the nullable
  // `rowId` column + the UNIQUE index. flightcrew is intentionally ABSENT from
  // ROWID_CURRENT_FLAG (it has no obsolete/deleted column) — so backfillRowIds
  // SKIPS it (existing historical signatures stay rowId=NULL), and the retire
  // sweep (hardcoded table list) never touches it.
  opsctrl_flightopscontroller_flightcrew: ["ident", "dutyFrom", "created"],
};

/**
 * The flightcrew (duty signature) rowId. Exists so the ONE write site
 * (focMain.authorizeDutyLimits) cannot drift from the key spec above.
 *
 * It did drift: the spec gained `created` on 2026-08-13 to make signing
 * repeatable, the write site kept composing the 2-part key, and nothing coupled
 * them — ROWID_KEY_SPEC is read only by the migration DDL and the backfill, and
 * rowid.spec.ts asserted the constant rather than the writer. The suite stayed
 * green while every re-sign silently OVERWROTE the previous signature, because
 * the server gate that used to reject the collision had been removed in the
 * same change on the strength of the key change that never shipped.
 *
 * Any other key derived from ROWID_KEY_SPEC should get the same treatment
 * rather than being hand-rolled at the call site.
 */
export function flightcrewRowId(ident: string, dutyFromMs: number, created: number): string {
  return rowIdFor([ident, dutyFromMs, created]);
}

/**
 * The soft-current flag column for each ROWID_KEY_SPEC table — the column the
 * obsolete=0 / deleted=0 reads filter on to find the single live row per logical
 * key. The 1-to-1 reports + collections use `obsolete`; param (flight INPUT data)
 * and crewmemberdata (A5 adhoc crew) use `deleted`. Used by the rowId backfill to
 * scope to current rows only — history rows stay rowId=NULL (they'd all collide
 * on the UNIQUE index, and the new model has no history: it upserts in place).
 */
export const ROWID_CURRENT_FLAG: Record<string, "obsolete" | "deleted"> = {
  opsctrl_flightopscontroller_nav: "obsolete",
  opsctrl_flightopscontroller_met: "obsolete",
  opsctrl_flightopscontroller_notam: "obsolete",
  opsctrl_flightopscontroller_fuel: "obsolete",
  opsctrl_flightopscontroller_perf: "obsolete",
  opsctrl_flightopscontroller_loading: "obsolete",
  opsctrl_flightopscontroller_navseg: "obsolete",
  opsctrl_flightopscontroller_loadingelements: "obsolete",
  opsctrl_flightopscontroller_param: "deleted",
  opsctrl_flightcrewcontroller_crewmemberdata: "deleted",
};
